import { type ReactNode, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { downloadChartPng } from "./utils";

interface ChartCardProps {
	/** Heading shown on the card, and the caption inside the downloaded image. */
	title: string;
	/** PNG filename without the extension. Kebab-case, no diacritics. */
	filename: string;
	/** Small caption under the heading, e.g. "Hiển thị tất cả 39 ngành". */
	description?: ReactNode;
	/** Icon before the heading, keeping the look of cards that had a CardHeader. */
	icon?: ReactNode;
	/** Extra buttons beside the download one, for charts with their own actions. */
	actions?: ReactNode;
	className?: string;
	contentClassName?: string;
	children: ReactNode;
}

/**
 * Wraps one chart and gives it its own PNG download button.
 *
 * Every statistics tab already has a "Tải PNG" button, but that one captures
 * the whole tab, so getting a single chart out of it meant cropping the image
 * by hand. This button captures exactly this `Card`, heading and caption
 * included, so the image explains itself once pasted into a report.
 *
 * The button row is HIDDEN during the capture; otherwise the download button
 * lands in the image. `visibility`, not `display`, so the space is kept and the
 * rest of the heading does not shift mid-capture.
 */
export function ChartCard({
	title,
	filename,
	description,
	icon,
	actions,
	className,
	contentClassName,
	children,
}: ChartCardProps) {
	const cardRef = useRef<HTMLDivElement>(null);
	const actionsRef = useRef<HTMLDivElement>(null);
	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownload = async () => {
		setIsDownloading(true);
		if (actionsRef.current) actionsRef.current.style.visibility = "hidden";

		// A chart with many categories lives in a horizontal scroller, and a capture
		// only sees what is on screen — without widening, the downloaded file loses
		// most of its bars. Widen the card to the content's real width for the
		// capture, and restore it in `finally` even when the capture fails.
		const scroller = cardRef.current?.querySelector<HTMLElement>(".overflow-x-auto") ?? null;
		const widened = scroller !== null && scroller.scrollWidth > scroller.clientWidth;
		const previous = {
			cardWidth: cardRef.current?.style.width ?? "",
			cardMaxWidth: cardRef.current?.style.maxWidth ?? "",
			scrollerOverflow: scroller?.style.overflowX ?? "",
		};
		if (widened && cardRef.current && scroller) {
			const extra = cardRef.current.offsetWidth - scroller.clientWidth; // side padding
			cardRef.current.style.width = `${scroller.scrollWidth + extra}px`;
			cardRef.current.style.maxWidth = "none";
			scroller.style.overflowX = "visible";
		}

		try {
			await downloadChartPng(cardRef, filename);
		} finally {
			if (widened && cardRef.current && scroller) {
				cardRef.current.style.width = previous.cardWidth;
				cardRef.current.style.maxWidth = previous.cardMaxWidth;
				scroller.style.overflowX = previous.scrollerOverflow;
			}
			if (actionsRef.current) actionsRef.current.style.visibility = "";
			setIsDownloading(false);
		}
	};

	return (
		<Card ref={cardRef} className={className}>
			<CardContent className={cn("p-6", contentClassName)}>
				{/* Heading and button share one row at a single h-8 height. */}
				<div className="mb-4 flex items-center justify-between gap-3">
					<div className="min-w-0">
						<h3 className="flex items-center gap-2 truncate text-base font-semibold md:text-lg">
							{icon}
							{title}
						</h3>
						{description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
					</div>
					<div ref={actionsRef} className="flex flex-shrink-0 items-center gap-2">
						{actions}
						<Button
							onClick={handleDownload}
							disabled={isDownloading}
							variant="outline"
							size="sm"
							className="h-8 gap-1.5"
							aria-label={`Tải ảnh biểu đồ ${title}`}
						>
							{isDownloading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Download className="h-4 w-4" />
							)}
							<span className="hidden sm:inline">
								{isDownloading ? "Đang tạo ảnh..." : "Tải PNG"}
							</span>
						</Button>
					</div>
				</div>
				{children}
			</CardContent>
		</Card>
	);
}
